import React, { useState, useEffect, useMemo, useCallback } from 'react';

// ==========================================
// TYPES & INTERFACES
// ==========================================
interface SocialAccount {
    id: string;
    platform: 'instagram' | 'facebook' | 'x' | 'linkedin' | 'tiktok';
    username: string;
    displayName: string;
    profileImage?: string;
    isConnected: boolean;
    isSyncing?: boolean;
    followers?: number;
}

interface ScheduledPost {
    id: string;
    title: string;
    content: string;
    platforms: string[];
    scheduledAt: Date;
    status: 'draft' | 'scheduled' | 'published' | 'failed';
    imageUrl?: string;
    hashtags?: string[];
    aiGenerated?: boolean;
}

interface ContentIdea {
    id: string;
    title: string;
    description: string;
    content?: string;
    hashtags?: string[];
    platform?: string;
    category?: 'destination' | 'deal' | 'tip' | 'seasonal';
}

interface SocialContainerProps {
    currentUser: any;
    theme: 'light' | 'dark';
    language: 'en' | 'sv';
}

// ==========================================
// INTEGRATOR CONFIG
// ==========================================
const INTEGRATOR_OPTIONS = [
    { id: 'ayrshare', name: 'Ayrshare', pricing: 'From $29/mo', platforms: ['instagram', 'facebook', 'x', 'linkedin', 'tiktok'] },
    { id: 'buffer', name: 'Buffer API', pricing: 'From $15/mo', platforms: ['instagram', 'facebook', 'x', 'linkedin'] },
    { id: 'publer', name: 'Publer API', pricing: 'From $12/mo', platforms: ['instagram', 'facebook', 'x', 'linkedin', 'tiktok'] }
];

// No mock data - modules should show empty state when API is not connected

// ==========================================
// PLATFORM ICONS
// ==========================================
const PlatformIcon: React.FC<{ platform: string; size?: number; showBg?: boolean }> = ({ platform, size = 48, showBg = true }) => {
    const configs: Record<string, { gradient: string; icon: string }> = {
        instagram: { gradient: 'from-pink-500 via-purple-500 to-orange-400', icon: '📸' },
        facebook: { gradient: 'from-blue-600 to-blue-500', icon: '📘' },
        x: { gradient: 'from-gray-900 to-gray-700', icon: '𝕏' },
        linkedin: { gradient: 'from-blue-700 to-blue-600', icon: '💼' },
        tiktok: { gradient: 'from-gray-900 via-pink-500 to-cyan-400', icon: '🎵' }
    };
    const cfg = configs[platform] || { gradient: 'from-gray-400 to-gray-500', icon: '🌐' };

    if (!showBg) return <span style={{ fontSize: size * 0.5 }}>{cfg.icon}</span>;

    return (
        <div
            className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${cfg.gradient} flex items-center justify-center text-white shadow-lg`}
            style={{ width: size, height: size }}
        >
            <span style={{ fontSize: size * 0.4 }}>{cfg.icon}</span>
        </div>
    );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const SocialContainer: React.FC<SocialContainerProps> = ({ currentUser, theme: initialTheme, language }) => {
    // Use theme from props
    const theme = initialTheme || 'light';
    const isDark = theme === 'dark';

    // API State
    const [apiConnected, setApiConnected] = useState(false);
    const [showIntegratorModal, setShowIntegratorModal] = useState(false);

    // Navigation
    const [activeTab, setActiveTab] = useState<'home' | 'plan' | 'inbox' | 'settings'>('home');
    const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);

    // Data - start empty and load from API
    const [accounts, setAccounts] = useState<SocialAccount[]>([]);
    const [posts, setPosts] = useState<ScheduledPost[]>([]);
    const [ideas, setIdeas] = useState<ContentIdea[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dataSource, setDataSource] = useState<'api' | 'mock'>('mock');

    // AI Generation State
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatingType, setGeneratingType] = useState<string>('');

    // API Key State
    const [selectedIntegrator, setSelectedIntegrator] = useState<string | null>(null);
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
    const [showLiveStreamModal, setShowLiveStreamModal] = useState(false);
    const [selectedStreamPlatform, setSelectedStreamPlatform] = useState<string>('instagram');
    const [streamStatus, setStreamStatus] = useState<'idle' | 'preparing' | 'live' | 'ended'>('idle');

    // Load data from API on mount
    useEffect(() => {
        const loadSocialData = async () => {
            setIsLoading(true);
            try {
                // Load posts
                const postsRes = await fetch('/api/social/posts');
                if (postsRes.ok) {
                    const postsData = await postsRes.json();
                    if (postsData.success) {
                        setPosts(postsData.posts.map((p: any) => ({
                            ...p,
                            id: p._id || p.id,
                            scheduledAt: new Date(p.scheduledAt)
                        })));
                        if (!postsData.fromMock) setDataSource('api');
                    }
                }

                // Load accounts
                const accountsRes = await fetch('/api/social/accounts');
                if (accountsRes.ok) {
                    const accountsData = await accountsRes.json();
                    if (accountsData.success) {
                        setAccounts(accountsData.accounts.map((a: any) => ({
                            ...a,
                            id: a._id || a.id
                        })));
                        if (!accountsData.fromMock) {
                            setDataSource('api');
                            setApiConnected(true);
                        }
                    }
                }

                // Load ideas
                const ideasRes = await fetch('/api/social/ideas');
                if (ideasRes.ok) {
                    const ideasData = await ideasRes.json();
                    if (ideasData.success) {
                        setIdeas(ideasData.ideas.map((i: any) => ({
                            ...i,
                            id: i._id || i.id
                        })));
                    }
                }
            } catch (error) {
                console.warn('[Social] API unavailable:', error);
                // Keep empty arrays - no mock data fallback
                setDataSource('mock');
            } finally {
                setIsLoading(false);
            }
        };
        loadSocialData();

        // Check if API key exists in localStorage
        const hasApiKey = INTEGRATOR_OPTIONS.some(opt =>
            localStorage.getItem(`social_api_${opt.id}`)
        );
        if (hasApiKey) {
            setApiConnected(true);
        }
    }, []);

    // Stats
    const stats = useMemo(() => ({
        totalReach: apiConnected ? '128.5K' : '0',
        reachChange: apiConnected ? '+14%' : '—',
        engagement: apiConnected ? '8.2%' : '0%',
        engagementChange: apiConnected ? '-2.1%' : '—',
        likes: apiConnected ? '24K' : '0',
        comments: apiConnected ? '1.2K' : '0'
    }), [apiConnected]);

    // Translations
    const t = useMemo(() => ({
        en: {
            welcomeBack: 'WELCOME BACK',
            socialHub: 'Social Hub',
            connectedAccounts: 'Connected Accounts',
            manage: 'Manage',
            quickActions: 'Quick Actions',
            newPost: 'New Post',
            schedule: 'Schedule',
            analytics: 'Analytics',
            goLive: 'Go Live',
            totalReach: 'TOTAL REACH',
            engagement: 'ENGAGEMENT',
            likes: 'Likes',
            comments: 'Comments',
            home: 'Home',
            plan: 'Plan',
            inbox: 'Inbox',
            settings: 'Settings',
            newCampaign: 'New Campaign',
            syncing: 'Syncing...',
            addNew: 'Add New',
            platform: 'Platform',
            generateWithAI: 'Generate with AI',
            generating: 'Generating...',
            mockDataNotice: 'No data flow. Connect an API integrator to enable live posting.',
            connectIntegrator: 'Connect API'
        },
        sv: {
            welcomeBack: 'VÄLKOMMEN TILLBAKA',
            socialHub: 'Sociala Hubben',
            connectedAccounts: 'Anslutna Konton',
            manage: 'Hantera',
            quickActions: 'Snabba Åtgärder',
            newPost: 'Nytt Inlägg',
            schedule: 'Schemalägg',
            analytics: 'Analys',
            goLive: 'Sänd Live',
            totalReach: 'RÄCKVIDD',
            engagement: 'ENGAGEMANG',
            likes: 'Likes',
            comments: 'Comments',
            home: 'Home',
            plan: 'Plan',
            inbox: 'Inbox',
            settings: 'Settings',
            newCampaign: 'New Campaign',
            syncing: 'Syncing...',
            addNew: 'Add New',
            platform: 'Platform',
            generateWithAI: 'Generera med AI',
            generating: 'Genererar...',
            mockDataNotice: 'Veri akışı yok. Canlı paylaşım için API bağlayın.',
            connectIntegrator: 'API Bağla'
        }
    }), []);
    const texts = t[language] || t.en;

    // Generate AI Content
    const generateAIIdeas = useCallback(async () => {
        setIsGenerating(true);
        setGeneratingType('ideas');

        // Simulate AI generation
        await new Promise(r => setTimeout(r, 2500));

        const newIdea: ContentIdea = {
            id: `ai_${Date.now()}`,
            title: 'AI Generated: Weekend Getaway',
            description: 'Perfect weekend escape packages for busy professionals',
            category: 'deal'
        };
        setIdeas(prev => [newIdea, ...prev]);
        setIsGenerating(false);
        setGeneratingType('');
    }, []);

    // Theme classes
    const bgMain = isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50';
    const bgCard = isDark ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white/80 border-gray-200/50';
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-slate-400' : 'text-gray-500';
    const accent = 'text-cyan-500';

    return (
        <div className={`flex flex-col h-full w-full font-sans overflow-hidden ${bgMain} ${textPrimary}`}>
            {/* Header */}
            <header className={`flex-shrink-0 px-6 py-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center">
                        <span className="text-white text-lg">🌐</span>
                    </div>
                    <div>
                        <h1 className="font-bold text-lg">Lumi Tours</h1>
                        <p className={`text-xs ${textSecondary}`}>Admin Panel</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Language Toggle */}
                    <div className="flex bg-gray-200/50 dark:bg-slate-700/50 rounded-lg p-1">
                        <button className={`px-3 py-1 text-sm rounded ${language === 'en' ? 'bg-cyan-500 text-white' : ''}`}>EN</button>
                        <button className={`px-3 py-1 text-sm rounded ${language === 'sv' ? 'bg-cyan-500 text-white' : ''}`}>SV</button>
                    </div>

                    {/* Notifications */}
                    <button className="relative p-2">
                        <span className="text-xl">🔔</span>
                        <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                    </button>

                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white font-bold">
                        {currentUser?.name?.[0] || 'A'}
                    </div>
                </div>
            </header>

            {/* Mock Data Notice */}
            {!apiConnected && (
                <div className="mx-6 mb-4 px-4 py-2 bg-amber-100/80 dark:bg-amber-900/30 border border-amber-300/50 dark:border-amber-700/50 rounded-xl flex items-center justify-between">
                    <span className="text-amber-700 dark:text-amber-400 text-sm flex items-center gap-2">
                        ⚠️ {texts.mockDataNotice}
                    </span>
                    <button
                        onClick={() => setShowIntegratorModal(true)}
                        className="px-3 py-1 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600"
                    >
                        {texts.connectIntegrator}
                    </button>
                </div>
            )}

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto px-6 pb-6" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                {activeTab === 'home' && (
                    <>
                        {/* Hero Banner */}
                        <div className="relative rounded-3xl overflow-hidden mb-6 bg-gradient-to-r from-slate-800 to-slate-700">
                            <div
                                className="absolute inset-0 bg-cover bg-center opacity-40"
                                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800)' }}
                            />
                            <div className="relative p-8 flex items-center justify-between">
                                <div>
                                    <p className={`text-xs font-bold tracking-widest mb-2 ${accent}`}>
                                        {texts.welcomeBack} / VÄLKOMMEN TILLBAKA
                                    </p>
                                    <h2 className="text-3xl font-black text-white mb-1">{texts.socialHub}</h2>
                                    <p className="text-white/70">Sociala Hubben</p>
                                </div>
                                <button
                                    onClick={() => setShowNewCampaignModal(true)}
                                    className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-xl flex items-center gap-2 transition-colors shadow-lg shadow-cyan-500/30"
                                >
                                    <span>+</span> {texts.newCampaign}
                                </button>
                            </div>
                        </div>

                        {/* Connected Accounts */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold">{texts.connectedAccounts} / Anslutna Konton</h3>
                                <button className={`text-sm font-medium ${accent}`}>{texts.manage} / Hantera</button>
                            </div>
                            <div className="grid grid-cols-4 gap-4">
                                {accounts.map(acc => (
                                    <div key={acc.id} className={`p-4 rounded-2xl border backdrop-blur-sm text-center ${bgCard}`}>
                                        <div className="flex justify-center mb-3">
                                            <PlatformIcon platform={acc.platform} />
                                        </div>
                                        <p className="font-bold text-sm">{acc.displayName}</p>
                                        <p className={`text-xs ${textSecondary}`}>
                                            {acc.isSyncing ? texts.syncing : `@${acc.username}`}
                                        </p>
                                    </div>
                                ))}
                                <div onClick={() => setShowIntegratorModal(true)} className={`p-4 rounded-2xl border-2 border-dashed text-center flex flex-col items-center justify-center cursor-pointer hover:border-cyan-400 transition-colors ${isDark ? 'border-slate-600' : 'border-gray-300'}`}>
                                    <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center mb-3">
                                        <span className="text-2xl text-gray-400">+</span>
                                    </div>
                                    <p className="font-bold text-sm">{texts.addNew}</p>
                                    <p className={`text-xs ${textSecondary}`}>{texts.platform}</p>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="mb-6">
                            <h3 className="font-bold mb-4">{texts.quickActions} / Snabba Åtgärder</h3>
                            <div className="grid grid-cols-4 gap-4">
                                {[
                                    { icon: '✏️', label: texts.newPost, sublabel: 'Nytt Inlägg', color: 'from-cyan-500 to-teal-500', action: () => setShowNewCampaignModal(true), active: true },
                                    { icon: '📅', label: texts.schedule, sublabel: 'Schemalägg', color: 'from-purple-500 to-pink-500', action: () => setShowScheduleModal(true) },
                                    { icon: '📊', label: texts.analytics, sublabel: 'Analys', color: 'from-blue-500 to-indigo-500', action: () => setShowAnalyticsModal(true) },
                                    { icon: '🔴', label: texts.goLive, sublabel: 'Sänd Live', color: 'from-red-500 to-orange-500', action: () => setShowLiveStreamModal(true) }
                                ].map((action, idx) => (
                                    <div
                                        key={idx}
                                        onClick={action.action}
                                        className={`p-4 rounded-2xl border backdrop-blur-sm cursor-pointer transition-all hover:scale-105 hover:shadow-lg
                                            ${action.active
                                                ? `bg-gradient-to-br ${action.color} text-white border-transparent shadow-lg`
                                                : `${bgCard} hover:border-cyan-400`
                                            }`}
                                    >
                                        <div className={`w-10 h-10 rounded-xl ${action.active ? 'bg-white/20' : 'bg-gray-100 dark:bg-slate-700'} flex items-center justify-center mb-3`}>
                                            <span className="text-xl">{action.icon}</span>
                                        </div>
                                        <p className="font-bold text-sm">{action.label}</p>
                                        <p className={`text-xs ${action.active ? 'text-white/70' : textSecondary}`}>{action.sublabel}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Stats Row */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            {/* Total Reach */}
                            <div className={`p-5 rounded-2xl border backdrop-blur-sm ${bgCard}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <p className={`text-xs font-bold tracking-wider ${textSecondary}`}>{texts.totalReach} / RÄCKVIDD</p>
                                    <span className="text-xs font-bold text-green-500 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full flex items-center gap-1">
                                        📈 {stats.reachChange}
                                    </span>
                                </div>
                                <p className="text-4xl font-black mb-4">{stats.totalReach}</p>
                                {/* Mini Chart */}
                                <div className="flex items-end gap-2 h-16">
                                    {[40, 50, 45, 55, 60, 100].map((h, i) => (
                                        <div
                                            key={i}
                                            className={`flex-1 rounded-t ${i === 5 ? 'bg-cyan-400' : 'bg-gray-200 dark:bg-slate-600'}`}
                                            style={{ height: `${h}%` }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Engagement */}
                            <div className={`p-5 rounded-2xl border backdrop-blur-sm ${bgCard}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <p className={`text-xs font-bold tracking-wider ${textSecondary}`}>{texts.engagement} / ENGAGEMANG</p>
                                    <span className="text-xs font-bold text-red-500 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded-full flex items-center gap-1">
                                        📉 {stats.engagementChange}
                                    </span>
                                </div>
                                <p className="text-4xl font-black mb-4">{stats.engagement}</p>

                                {/* Progress Bars */}
                                <div className="space-y-3">
                                    <div>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className={textSecondary}>{texts.likes}</span>
                                            <span className="font-bold">{stats.likes}</span>
                                        </div>
                                        <div className="h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                                            <div className="h-full w-3/4 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full" />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className={textSecondary}>{texts.comments}</span>
                                            <span className="font-bold">{stats.comments}</span>
                                        </div>
                                        <div className="h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                                            <div className="h-full w-1/2 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* AI Content Ideas */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold">AI Content Ideas / AI Innehållsidéer</h3>
                                <button
                                    onClick={generateAIIdeas}
                                    disabled={isGenerating}
                                    className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${isGenerating
                                        ? 'bg-gray-200 text-gray-500 cursor-wait'
                                        : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg'
                                        }`}
                                >
                                    {isGenerating ? (
                                        <><span className="animate-spin">⏳</span> {texts.generating}</>
                                    ) : (
                                        <>✨ {texts.generateWithAI}</>
                                    )}
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                {ideas.slice(0, 4).map(idea => (
                                    <div key={idea.id} className={`p-4 rounded-2xl border backdrop-blur-sm cursor-pointer hover:border-purple-400 transition-all ${bgCard}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${idea.category === 'destination' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                idea.category === 'deal' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                    idea.category === 'seasonal' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                                        'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                                }`}>
                                                {idea.category}
                                            </span>
                                            {idea.id.startsWith('ai_') && (
                                                <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 px-2 py-0.5 rounded-full">AI ✨</span>
                                            )}
                                        </div>
                                        <h4 className="font-bold text-sm mb-1">{idea.title}</h4>
                                        <p className={`text-xs ${textSecondary}`}>{idea.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Scheduled Posts */}
                        <div>
                            <h3 className="font-bold mb-4">Upcoming Posts / Kommande Inlägg</h3>
                            <div className="space-y-3">
                                {posts.map(post => (
                                    <div key={post.id} className={`p-4 rounded-2xl border backdrop-blur-sm flex items-center gap-4 ${bgCard}`}>
                                        {post.imageUrl && (
                                            <img src={post.imageUrl} alt="" className="w-16 h-16 rounded-xl object-cover" />
                                        )}
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                {post.platforms.map(p => (
                                                    <span key={p} className="text-xs bg-pink-500 text-white px-2 py-0.5 rounded uppercase font-bold">
                                                        {p}
                                                    </span>
                                                ))}
                                                <span className={`text-xs ${textSecondary}`}>
                                                    {new Date(post.scheduledAt).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', {
                                                        weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                                    })}
                                                </span>
                                            </div>
                                            <h4 className="font-bold">{post.title}</h4>
                                            <p className={`text-sm ${textSecondary} truncate`}>{post.content}</p>
                                        </div>
                                        <button className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                                            <span className="text-xl">⋮</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'plan' && (
                    <div className="py-8">
                        <h2 className="text-2xl font-bold mb-6">Content Calendar / Innehållskalender</h2>
                        <div className={`p-6 rounded-2xl border ${bgCard}`}>
                            <div className="grid grid-cols-7 gap-2 mb-4">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                    <div key={day} className={`text-center text-xs font-bold ${textSecondary} py-2`}>{day}</div>
                                ))}
                                {Array.from({ length: 28 }, (_, i) => (
                                    <div key={i} className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm cursor-pointer transition-all hover:bg-cyan-500/20 ${i === 14 || i === 21 ? 'bg-cyan-500/20 border-2 border-cyan-500' : isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                                        <span className="font-bold">{i + 1}</span>
                                        {(i === 14 || i === 21) && <span className="text-[8px] text-cyan-500">2 posts</span>}
                                    </div>
                                ))}
                            </div>
                            <div className="border-t pt-4 mt-4">
                                <h4 className="font-bold mb-3">Scheduled for this week</h4>
                                {posts.slice(0, 2).map(post => (
                                    <div key={post.id} className={`p-3 rounded-xl mb-2 flex items-center gap-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                                        <span className="text-2xl">📅</span>
                                        <div>
                                            <p className="font-bold text-sm">{post.title}</p>
                                            <p className={`text-xs ${textSecondary}`}>{post.platforms.join(', ')}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'inbox' && (
                    <div className="py-8">
                        <h2 className="text-2xl font-bold mb-6">Inbox / Inkorg</h2>
                        <div className="space-y-3">
                            {[
                                { platform: 'instagram', user: '@travellover92', message: 'Love your Antalya packages! 🌴', time: '2 min ago', unread: true },
                                { platform: 'facebook', user: 'Maria S.', message: 'When is the next sale?', time: '1 hour ago', unread: true },
                                { platform: 'instagram', user: '@summer_vibes', message: 'Great photos! ☀️', time: '3 hours ago', unread: false },
                                { platform: 'x', user: '@adventure_seekers', message: 'Can you share more about Maldives?', time: '1 day ago', unread: false }
                            ].map((msg, i) => (
                                <div key={i} className={`p-4 rounded-2xl border flex items-center gap-4 cursor-pointer transition-all hover:border-cyan-400 ${msg.unread ? (isDark ? 'bg-cyan-900/20 border-cyan-500/50' : 'bg-cyan-50 border-cyan-200') : bgCard}`}>
                                    <PlatformIcon platform={msg.platform} size={40} />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <p className="font-bold text-sm">{msg.user}</p>
                                            {msg.unread && <span className="w-2 h-2 bg-cyan-500 rounded-full" />}
                                        </div>
                                        <p className={`text-sm ${textSecondary} truncate`}>{msg.message}</p>
                                    </div>
                                    <span className={`text-xs ${textSecondary}`}>{msg.time}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="py-8">
                        <h2 className="text-2xl font-bold mb-6">Settings / Inställningar</h2>
                        <div className="space-y-4">
                            {/* Connected Accounts Section */}
                            <div className={`p-6 rounded-2xl border ${bgCard}`}>
                                <h3 className="font-bold mb-4 flex items-center gap-2">🔗 Connected Accounts / Anslutna Konton</h3>
                                <div className="space-y-3">
                                    {accounts.map(acc => (
                                        <div key={acc.id} className={`p-3 rounded-xl flex items-center justify-between ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                                            <div className="flex items-center gap-3">
                                                <PlatformIcon platform={acc.platform} size={36} />
                                                <div>
                                                    <p className="font-bold text-sm">{acc.displayName}</p>
                                                    <p className={`text-xs ${textSecondary}`}>@{acc.username}</p>
                                                </div>
                                            </div>
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${acc.isConnected ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                                                {acc.isConnected ? 'Connected' : 'Syncing...'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => setShowIntegratorModal(true)} className="mt-4 w-full py-3 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 text-sm font-medium hover:border-cyan-400 transition-colors">
                                    + Add New Platform / Lägg till Ny Plattform
                                </button>
                            </div>

                            {/* API Settings */}
                            <div className={`p-6 rounded-2xl border ${bgCard}`}>
                                <h3 className="font-bold mb-4 flex items-center gap-2">🔑 API Configuration / API-konfiguration</h3>
                                <button onClick={() => setShowIntegratorModal(true)} className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-bold hover:shadow-lg transition-all">
                                    Configure API Keys / Konfigurera API-nycklar
                                </button>
                            </div>

                            {/* Preferences */}
                            <div className={`p-6 rounded-2xl border ${bgCard}`}>
                                <h3 className="font-bold mb-4 flex items-center gap-2">⚙️ Preferences / Preferenser</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm">Auto-schedule posts</span>
                                        <div className="w-12 h-6 bg-cyan-500 rounded-full relative cursor-pointer">
                                            <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm">AI content suggestions</span>
                                        <div className="w-12 h-6 bg-cyan-500 rounded-full relative cursor-pointer">
                                            <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm">Email notifications</span>
                                        <div className="w-12 h-6 bg-gray-300 dark:bg-slate-600 rounded-full relative cursor-pointer">
                                            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Bottom Navigation - Use sticky instead of fixed to prevent overlay issues */}
            <nav className={`sticky bottom-0 mt-auto w-full px-6 py-4 ${isDark ? 'bg-slate-900/95' : 'bg-white/95'} backdrop-blur-lg border-t ${isDark ? 'border-slate-700' : 'border-gray-200'} z-40`}>
                <div className="flex items-center justify-around">
                    {[
                        { id: 'home', icon: '🏠', label: texts.home },
                        { id: 'plan', icon: '📅', label: texts.plan },
                        { id: 'create', icon: '+', label: '', isCenter: true },
                        { id: 'inbox', icon: '✉️', label: texts.inbox },
                        { id: 'settings', icon: '⚙️', label: texts.settings }
                    ].map(item => (
                        item.isCenter ? (
                            <button
                                key={item.id}
                                onClick={() => setShowNewCampaignModal(true)}
                                className="w-14 h-14 -mt-8 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-cyan-500/30 hover:scale-110 transition-transform"
                            >
                                +
                            </button>
                        ) : (
                            <button
                                key={item.id}
                                onClick={() => setActiveTab(item.id as any)}
                                className={`flex flex-col items-center gap-1 ${activeTab === item.id ? accent : textSecondary}`}
                            >
                                <span className="text-xl">{item.icon}</span>
                                <span className="text-xs">{item.label}</span>
                            </button>
                        )
                    ))}
                </div>
            </nav>

            {/* Integrator Modal */}
            {showIntegratorModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowIntegratorModal(false); setSelectedIntegrator(null); setApiKeyInput(''); }}>
                    <div onClick={(e) => e.stopPropagation()} className={`w-full max-w-md rounded-3xl p-6 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                        <h2 className="text-xl font-bold mb-4">Connect Social Media API</h2>

                        {!selectedIntegrator ? (
                            <>
                                <p className={`text-sm ${textSecondary} mb-6`}>Choose an integrator to enable live posting</p>
                                <div className="space-y-3">
                                    {INTEGRATOR_OPTIONS.map(opt => (
                                        <button
                                            key={opt.id}
                                            onClick={() => setSelectedIntegrator(opt.id)}
                                            className={`w-full p-4 rounded-2xl border-2 text-left transition-all hover:border-cyan-500 ${bgCard}`}
                                        >
                                            <div className="flex justify-between items-center">
                                                <span className="font-bold">{opt.name}</span>
                                                <span className="text-green-600 text-sm font-medium">{opt.pricing}</span>
                                            </div>
                                            <div className="flex gap-1 mt-2">
                                                {opt.platforms.map(p => <PlatformIcon key={p} platform={p} size={24} />)}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => setSelectedIntegrator(null)}
                                    className={`mb-4 flex items-center gap-2 text-sm ${textSecondary} hover:${textPrimary}`}
                                >
                                    ← Back to integrators
                                </button>
                                <div className={`p-4 rounded-2xl mb-4 ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                                    <p className="font-bold mb-1">{INTEGRATOR_OPTIONS.find(o => o.id === selectedIntegrator)?.name}</p>
                                    <p className={`text-xs ${textSecondary}`}>Enter your API key to connect</p>
                                </div>
                                <div className="mb-4">
                                    <label className={`text-sm font-medium ${textSecondary} mb-2 block`}>API Key</label>
                                    <input
                                        type="text"
                                        value={apiKeyInput}
                                        onChange={(e) => setApiKeyInput(e.target.value)}
                                        placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxx"
                                        className={`w-full p-3 rounded-xl border font-mono text-sm ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-200'}`}
                                    />
                                </div>
                                <button
                                    onClick={async () => {
                                        if (apiKeyInput.length > 10) {
                                            try {
                                                const res = await fetch('/api/social/accounts/connect', {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({ 
                                                        platform: 'ayrshare', 
                                                        apiKey: apiKeyInput, 
                                                        username: 'Lumitours', // To be fetched from service
                                                        displayName: selectedIntegrator || 'Ayrshare'
                                                    })
                                                });
                                                const data = await res.json();
                                                
                                                if (data.success) {
                                                    alert('API Key saved to backend. Social integration is now LIVE.');
                                                    setApiConnected(true);
                                                    setShowIntegratorModal(false);
                                                    setSelectedIntegrator(null);
                                                    setApiKeyInput('');
                                                    // Refresh accounts
                                                    window.location.reload();
                                                }
                                            } catch (e) {
                                                alert('Failed to connect API: ' + (e as Error).message);
                                            }
                                        } else {
                                            alert('Please enter a valid API key');
                                        }
                                    }}
                                    className="w-full py-3 rounded-xl font-medium bg-gradient-to-r from-cyan-500 to-teal-500 text-white hover:shadow-lg transition-all"
                                >
                                    Connect & Save to Backend
                                </button>
                            </>
                        )}

                        <button
                            onClick={() => { setShowIntegratorModal(false); setSelectedIntegrator(null); setApiKeyInput(''); }}
                            className={`w-full mt-4 py-3 rounded-xl font-medium ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Schedule Modal - Full Calendar View */}
            {showScheduleModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowScheduleModal(false)}>
                    <div onClick={(e) => e.stopPropagation()} className={`w-full max-w-4xl rounded-3xl p-6 max-h-[90vh] overflow-y-auto ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold">📅 Content Calendar / Innehållskalender</h2>
                            <button onClick={() => setShowScheduleModal(false)} className="text-2xl hover:text-red-500 transition-colors">×</button>
                        </div>

                        {/* Month Navigation */}
                        <div className="flex items-center justify-between mb-6">
                            <button className={`p-2 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>◀</button>
                            <h3 className="text-lg font-bold">January 2025</h3>
                            <button className={`p-2 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>▶</button>
                        </div>

                        {/* Calendar Grid */}
                        <div className={`p-4 rounded-2xl mb-6 ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                            <div className="grid grid-cols-7 gap-2 mb-3">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                    <div key={day} className={`text-center text-xs font-bold ${textSecondary} py-2`}>{day}</div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 gap-2">
                                {Array.from({ length: 35 }, (_, i) => {
                                    const day = i - 2; // Start from -2 for previous month overflow
                                    const isCurrentMonth = day >= 1 && day <= 31;
                                    const hasPost = [5, 12, 15, 18, 22, 28].includes(day);
                                    const isToday = day === 28;
                                    return (
                                        <div
                                            key={i}
                                            className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm cursor-pointer transition-all relative
                                                ${!isCurrentMonth ? 'opacity-30' : ''}
                                                ${isToday ? 'ring-2 ring-cyan-500' : ''}
                                                ${hasPost ? 'bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30' : isDark ? 'bg-slate-600/50 hover:bg-slate-600' : 'bg-white hover:bg-gray-100'}
                                            `}
                                        >
                                            <span className="font-bold">{day > 0 && day <= 31 ? day : day <= 0 ? 31 + day : day - 31}</span>
                                            {hasPost && <span className="text-[8px] text-cyan-400 font-bold">📝</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Scheduled Posts List */}
                        <div>
                            <h4 className="font-bold mb-3">Upcoming Posts / Kommande Inlägg</h4>
                            <div className="space-y-3">
                                {posts.slice(0, 3).map(post => (
                                    <div key={post.id} className={`p-4 rounded-xl flex items-center gap-4 ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                                        <div className="flex gap-1">
                                            {post.platforms.map(p => <PlatformIcon key={p} platform={p} size={28} />)}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-bold text-sm">{post.title}</p>
                                            <p className={`text-xs ${textSecondary}`}>
                                                {new Date(post.scheduledAt).toLocaleDateString('sv-SE', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </div>
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${post.status === 'scheduled' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-green-500/20 text-green-400'}`}>
                                            {post.status}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={() => { setShowScheduleModal(false); setShowNewCampaignModal(true); }}
                            className="w-full mt-6 py-3 rounded-xl font-bold bg-gradient-to-r from-cyan-500 to-teal-500 text-white hover:shadow-lg transition-all"
                        >
                            + Schedule New Post
                        </button>
                    </div>
                </div>
            )}

            {/* Analytics Modal - Full Dashboard */}
            {showAnalyticsModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAnalyticsModal(false)}>
                    <div onClick={(e) => e.stopPropagation()} className={`w-full max-w-4xl rounded-3xl p-6 max-h-[90vh] overflow-y-auto ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold">📊 Analytics Dashboard / Analysöversikt</h2>
                            <button onClick={() => setShowAnalyticsModal(false)} className="text-2xl hover:text-red-500 transition-colors">×</button>
                        </div>

                        {/* KPI Cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            {[
                                { label: 'Total Reach', value: '128.5K', change: '+14%', positive: true, icon: '👁️' },
                                { label: 'Engagement', value: '8.2%', change: '-2.1%', positive: false, icon: '💬' },
                                { label: 'Followers', value: '24.8K', change: '+856', positive: true, icon: '👥' },
                                { label: 'Posts', value: '47', change: '+12', positive: true, icon: '📝' }
                            ].map((kpi, i) => (
                                <div key={i} className={`p-4 rounded-2xl ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xl">{kpi.icon}</span>
                                        <span className={`text-xs font-bold ${textSecondary}`}>{kpi.label}</span>
                                    </div>
                                    <p className="text-2xl font-black">{kpi.value}</p>
                                    <span className={`text-xs font-bold ${kpi.positive ? 'text-green-500' : 'text-red-500'}`}>
                                        {kpi.positive ? '📈' : '📉'} {kpi.change}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Engagement Chart */}
                        <div className={`p-5 rounded-2xl mb-6 ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                            <h3 className="font-bold mb-4">Engagement Over Time / Engagemang Över Tid</h3>
                            <div className="flex items-end gap-2 h-40">
                                {[45, 62, 55, 78, 82, 68, 95, 88, 72, 85, 91, 100].map((h, i) => (
                                    <div key={i} className="flex-1 flex flex-col items-center">
                                        <div
                                            className="w-full bg-gradient-to-t from-cyan-500 to-teal-400 rounded-t-lg transition-all hover:opacity-80"
                                            style={{ height: `${h}%` }}
                                        />
                                        <span className={`text-[10px] mt-1 ${textSecondary}`}>{['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Platform Breakdown */}
                        <div className={`p-5 rounded-2xl ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                            <h3 className="font-bold mb-4">Platform Performance / Plattformsprestanda</h3>
                            <div className="space-y-4">
                                {[
                                    { platform: 'instagram', followers: '12.5K', engagement: '9.2%', posts: 24 },
                                    { platform: 'facebook', followers: '8.2K', engagement: '6.8%', posts: 18 },
                                    { platform: 'tiktok', followers: '4.1K', engagement: '12.5%', posts: 5 }
                                ].map((p, i) => (
                                    <div key={i} className={`flex items-center gap-4 p-3 rounded-xl ${isDark ? 'bg-slate-600' : 'bg-white'}`}>
                                        <PlatformIcon platform={p.platform} size={40} />
                                        <div className="flex-1">
                                            <p className="font-bold capitalize">{p.platform}</p>
                                            <p className={`text-xs ${textSecondary}`}>{p.posts} posts this month</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-cyan-400">{p.followers}</p>
                                            <p className={`text-xs ${textSecondary}`}>{p.engagement} eng.</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Live Stream Modal */}
            {showLiveStreamModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowLiveStreamModal(false)}>
                    <div onClick={(e) => e.stopPropagation()} className={`w-full max-w-xl rounded-3xl p-6 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                {streamStatus === 'live' && <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />}
                                🔴 Go Live / Sänd Live
                            </h2>
                            <button onClick={() => { setShowLiveStreamModal(false); setStreamStatus('idle'); }} className="text-2xl hover:text-red-500 transition-colors">×</button>
                        </div>

                        {streamStatus === 'idle' && (
                            <>
                                {/* Platform Selection */}
                                <div className="mb-6">
                                    <label className={`text-sm font-medium ${textSecondary} mb-3 block`}>Select Platform / Välj Plattform</label>
                                    <div className="grid grid-cols-3 gap-3">
                                        {['instagram', 'facebook', 'tiktok'].map(p => (
                                            <button
                                                key={p}
                                                onClick={() => setSelectedStreamPlatform(p)}
                                                className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${selectedStreamPlatform === p ? 'border-cyan-500 bg-cyan-500/10' : isDark ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'}`}
                                            >
                                                <PlatformIcon platform={p} size={40} />
                                                <span className="text-sm font-bold capitalize">{p}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Stream Title */}
                                <div className="mb-6">
                                    <label className={`text-sm font-medium ${textSecondary} mb-2 block`}>Stream Title / Titel</label>
                                    <input
                                        type="text"
                                        placeholder="Enter your stream title..."
                                        className={`w-full p-3 rounded-xl border ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}
                                    />
                                </div>

                                {/* Stream Key */}
                                <div className={`p-4 rounded-2xl mb-6 ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`text-sm font-medium ${textSecondary}`}>Stream Key (optional)</span>
                                        <span className="text-xs text-cyan-400 cursor-pointer">Get key →</span>
                                    </div>
                                    <input
                                        type="password"
                                        placeholder="rtmp://..."
                                        className={`w-full p-2 rounded-lg text-sm font-mono ${isDark ? 'bg-slate-600' : 'bg-white border border-gray-200'}`}
                                    />
                                </div>

                                <button
                                    onClick={() => setStreamStatus('preparing')}
                                    className="w-full py-4 rounded-xl font-bold bg-gradient-to-r from-red-500 to-orange-500 text-white hover:shadow-lg hover:shadow-red-500/30 transition-all flex items-center justify-center gap-2"
                                >
                                    <span className="w-3 h-3 bg-white rounded-full" />
                                    Start Live Stream
                                </button>
                            </>
                        )}

                        {streamStatus === 'preparing' && (
                            <div className="text-center py-8">
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center animate-pulse">
                                    <span className="text-3xl">📡</span>
                                </div>
                                <h3 className="text-lg font-bold mb-2">Preparing Stream...</h3>
                                <p className={textSecondary}>Connecting to {selectedStreamPlatform}</p>
                                <div className="mt-6 flex gap-3">
                                    <button onClick={() => setStreamStatus('idle')} className={`flex-1 py-3 rounded-xl ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>Cancel</button>
                                    <button onClick={() => setStreamStatus('live')} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold">Go Live Now</button>
                                </div>
                            </div>
                        )}

                        {streamStatus === 'live' && (
                            <div className="text-center py-8">
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/50">
                                    <span className="w-4 h-4 bg-white rounded-full animate-ping" />
                                </div>
                                <h3 className="text-lg font-bold mb-2 text-red-500">🔴 YOU ARE LIVE</h3>
                                <p className={textSecondary}>Streaming to {selectedStreamPlatform}</p>
                                <div className={`mt-6 p-4 rounded-xl ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                                    <div className="grid grid-cols-3 gap-4 text-center">
                                        <div><p className="text-2xl font-black">0:42</p><p className={`text-xs ${textSecondary}`}>Duration</p></div>
                                        <div><p className="text-2xl font-black">127</p><p className={`text-xs ${textSecondary}`}>Viewers</p></div>
                                        <div><p className="text-2xl font-black">24</p><p className={`text-xs ${textSecondary}`}>Comments</p></div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => { setStreamStatus('ended'); setTimeout(() => { setStreamStatus('idle'); setShowLiveStreamModal(false); }, 2000); }}
                                    className="w-full mt-6 py-4 rounded-xl font-bold bg-slate-600 text-white hover:bg-slate-500 transition-all"
                                >
                                    End Stream
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* New Campaign Modal */}
            {showNewCampaignModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className={`w-full max-w-lg rounded-3xl p-6 max-h-[90vh] overflow-y-auto ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold">Create New Campaign</h2>
                            <button onClick={() => setShowNewCampaignModal(false)} className="text-2xl">×</button>
                        </div>

                        {/* Platform Selection */}
                        <div className="mb-6">
                            <label className={`text-sm font-medium ${textSecondary} mb-2 block`}>Select Platforms</label>
                            <div className="flex gap-2 flex-wrap">
                                {['instagram', 'facebook', 'x', 'linkedin', 'tiktok'].map(p => (
                                    <button key={p} className={`px-4 py-2 rounded-xl border-2 transition-all ${bgCard} hover:border-cyan-500`}>
                                        <PlatformIcon platform={p} size={24} />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="mb-6">
                            <label className={`text-sm font-medium ${textSecondary} mb-2 block`}>Content</label>
                            <textarea
                                className={`w-full h-32 p-4 rounded-xl border resize-none ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}
                                placeholder="Write your post content..."
                            />
                            <button className={`mt-2 text-sm font-medium ${accent} flex items-center gap-1`}>
                                ✨ Generate with AI
                            </button>
                        </div>

                        {/* Schedule */}
                        <div className="mb-6">
                            <label className={`text-sm font-medium ${textSecondary} mb-2 block`}>Schedule</label>
                            <input
                                type="datetime-local"
                                className={`w-full p-3 rounded-xl border ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3">
                            <button className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                Save Draft
                            </button>
                            <button className="flex-1 py-3 rounded-xl font-medium bg-gradient-to-r from-cyan-500 to-teal-500 text-white">
                                Schedule Post
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SocialContainer;
